import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#be185d",
  "#ca8a04",
  "#4f46e5",
  "#059669",
  "#c2410c",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#65a30d",
];

const LINE_MODE_RESTAURANTS = "restaurants";
const LINE_MODE_DEVICES = "devices";

const restaurantKey = (id) => `r-${id}`;
const deviceKey = (id) => `d-${id}`;

const round3 = (value) => Math.round(value * 1000) / 1000;

const formatKwh = (value) =>
  typeof value === "number"
    ? value.toLocaleString("ru-RU", {
        maximumFractionDigits: 3,
        minimumFractionDigits: 0,
      })
    : value;

const dayFromHourLabel = (label) => String(label || "").slice(0, 5);

function buildDayBuckets(hours) {
  const buckets = [];
  (hours || []).forEach((label, index) => {
    const day = dayFromHourLabel(label);
    if (!day) {
      return;
    }
    const last = buckets[buckets.length - 1];
    if (!last || last.day !== day) {
      buckets.push({ day, start: index, end: index });
      return;
    }
    last.end = index;
  });
  return buckets;
}

function sumHourlyRange(hourly, start, end) {
  let total = 0;
  for (let index = start; index <= end; index += 1) {
    total += Number(hourly?.[index]) || 0;
  }
  return round3(total);
}

const ChartTooltip = ({ active, payload, label, names }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="z-report-chart-tooltip">
      <p className="z-report-chart-tooltip-date">{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          className="z-report-chart-tooltip-row"
          style={{ color: entry.color }}
        >
          <span>{names[entry.dataKey] || entry.name}</span>
          <strong>{formatKwh(entry.value)} кВт·ч</strong>
        </p>
      ))}
    </div>
  );
};

const CompareTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  return (
    <div className="z-report-chart-tooltip">
      <p className="z-report-chart-tooltip-date">{item?.name}</p>
      <p className="z-report-chart-tooltip-row">
        <span>Расход</span>
        <strong>{formatKwh(item?.value)} кВт·ч</strong>
      </p>
    </div>
  );
};

const EnergyReportChart = ({ report }) => {
  const restaurants = report?.restaurants || [];
  const devices = report?.devices || [];
  const hours = report?.hours || [];

  const [visibleRestaurantIds, setVisibleRestaurantIds] = useState({});
  const [visibleDeviceIds, setVisibleDeviceIds] = useState({});
  const [lineMode, setLineMode] = useState(LINE_MODE_RESTAURANTS);

  useEffect(() => {
    if (!restaurants.length && !devices.length) {
      setVisibleRestaurantIds({});
      setVisibleDeviceIds({});
      setLineMode(LINE_MODE_RESTAURANTS);
      return;
    }

    const nextRestaurants = {};
    restaurants.forEach((restaurant) => {
      nextRestaurants[restaurant.restaurant_id] = true;
    });
    setVisibleRestaurantIds(nextRestaurants);

    const nextDevices = {};
    devices.forEach((device) => {
      nextDevices[device.device_id] = true;
    });
    setVisibleDeviceIds(nextDevices);
    setLineMode(LINE_MODE_RESTAURANTS);
  }, [report]);

  const dayBuckets = useMemo(() => buildDayBuckets(hours), [hours]);

  const selectedDevices = useMemo(
    () => devices.filter((device) => visibleDeviceIds[device.device_id]),
    [devices, visibleDeviceIds],
  );

  const devicesByRestaurant = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    restaurants.forEach((restaurant) => {
      const group = {
        restaurant_id: restaurant.restaurant_id,
        restaurant_name: restaurant.restaurant_name,
        devices: [],
      };
      groupMap.set(restaurant.restaurant_id, group);
      groups.push(group);
    });

    devices.forEach((device) => {
      let group = groupMap.get(device.restaurant_id);
      if (!group) {
        group = {
          restaurant_id: device.restaurant_id,
          restaurant_name: device.restaurant_name,
          devices: [],
        };
        groupMap.set(device.restaurant_id, group);
        groups.push(group);
      }
      group.devices.push(device);
    });

    return groups.filter((group) => group.devices.length);
  }, [restaurants, devices]);

  const restaurantColorIndex = useMemo(() => {
    const map = {};
    restaurants.forEach((restaurant, index) => {
      map[restaurant.restaurant_id] = index;
    });
    return map;
  }, [restaurants]);

  const deviceColorIndex = useMemo(() => {
    const map = {};
    devices.forEach((device, index) => {
      map[device.device_id] = index;
    });
    return map;
  }, [devices]);

  const seriesNames = useMemo(() => {
    const map = {};
    restaurants.forEach((restaurant) => {
      map[restaurantKey(restaurant.restaurant_id)] = restaurant.restaurant_name;
    });
    devices.forEach((device) => {
      map[deviceKey(device.device_id)] =
        `${device.restaurant_name} — ${device.device_name}`;
    });
    return map;
  }, [restaurants, devices]);

  const dailyChartData = useMemo(() => {
    if (!dayBuckets.length) {
      return [];
    }

    return dayBuckets.map((bucket) => {
      const row = { date: bucket.day };

      restaurants.forEach((restaurant) => {
        const total = selectedDevices
          .filter((device) => device.restaurant_id === restaurant.restaurant_id)
          .reduce(
            (sum, device) =>
              sum + sumHourlyRange(device.hourly, bucket.start, bucket.end),
            0,
          );
        row[restaurantKey(restaurant.restaurant_id)] = round3(total);
      });

      devices.forEach((device) => {
        row[deviceKey(device.device_id)] = sumHourlyRange(
          device.hourly,
          bucket.start,
          bucket.end,
        );
      });

      return row;
    });
  }, [dayBuckets, restaurants, devices, selectedDevices]);

  const comparisonData = useMemo(() => {
    return restaurants
      .map((restaurant, index) => {
        const total = selectedDevices
          .filter((device) => device.restaurant_id === restaurant.restaurant_id)
          .reduce((sum, device) => sum + (Number(device.total_diff) || 0), 0);
        return {
          restaurant_id: restaurant.restaurant_id,
          name: restaurant.restaurant_name,
          value: round3(total),
          color: CHART_COLORS[index % CHART_COLORS.length],
        };
      })
      .filter((item) => item.value > 0)
      .sort((left, right) => right.value - left.value);
  }, [restaurants, selectedDevices]);

  const visibleRestaurants = restaurants.filter(
    (restaurant) => visibleRestaurantIds[restaurant.restaurant_id],
  );

  const visibleDevices = devices.filter(
    (device) => visibleDeviceIds[device.device_id],
  );

  const dailySeries =
    lineMode === LINE_MODE_DEVICES
      ? visibleDevices.map((device) => ({
          key: deviceKey(device.device_id),
          color:
            CHART_COLORS[
              (deviceColorIndex[device.device_id] ?? 0) % CHART_COLORS.length
            ],
        }))
      : visibleRestaurants.map((restaurant) => ({
          key: restaurantKey(restaurant.restaurant_id),
          color:
            CHART_COLORS[
              (restaurantColorIndex[restaurant.restaurant_id] ?? 0) %
                CHART_COLORS.length
            ],
        }));

  const toggleRestaurant = (restaurantId) => {
    setVisibleRestaurantIds((prev) => ({
      ...prev,
      [restaurantId]: !prev[restaurantId],
    }));
  };

  const toggleDevice = (deviceId) => {
    setVisibleDeviceIds((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId],
    }));
  };

  const setAllRestaurantsVisible = (value) => {
    const next = {};
    restaurants.forEach((restaurant) => {
      next[restaurant.restaurant_id] = value;
    });
    setVisibleRestaurantIds(next);
  };

  const setAllDevicesVisible = (value) => {
    const next = {};
    devices.forEach((device) => {
      next[device.device_id] = value;
    });
    setVisibleDeviceIds(next);
  };

  if (!restaurants.length && !devices.length) {
    return null;
  }

  const dailyEmptyMessage =
    lineMode === LINE_MODE_DEVICES
      ? "Выберите хотя бы один счётчик для графика"
      : "Выберите хотя бы один ресторан для графика";

  return (
    <div className="energy-charts">
      <section className="z-report-chart-section">
        <div className="z-report-chart-header">
          <h4 className="z-report-chart-title">Расход по дням</h4>
          <div className="z-report-chart-actions">
            <div className="energy-chart-mode" role="group" aria-label="Линии графика">
              <button
                type="button"
                className={`energy-chart-mode-btn${
                  lineMode === LINE_MODE_RESTAURANTS ? " is-active" : ""
                }`}
                onClick={() => setLineMode(LINE_MODE_RESTAURANTS)}
              >
                Рестораны
              </button>
              <button
                type="button"
                className={`energy-chart-mode-btn${
                  lineMode === LINE_MODE_DEVICES ? " is-active" : ""
                }`}
                onClick={() => setLineMode(LINE_MODE_DEVICES)}
              >
                Счётчики
              </button>
            </div>
          </div>
        </div>

        <div className="energy-chart-filter-block">
          <div className="energy-chart-filter-head">
            <span className="energy-chart-filter-label">Рестораны</span>
            <div className="z-report-chart-actions">
              <button
                type="button"
                className="z-report-ghost-btn"
                onClick={() => setAllRestaurantsVisible(true)}
              >
                Включить все
              </button>
              <button
                type="button"
                className="z-report-ghost-btn"
                onClick={() => setAllRestaurantsVisible(false)}
              >
                Выключить все
              </button>
            </div>
          </div>
          <div className="z-report-chart-filters">
            {restaurants.map((restaurant, index) => {
              const color = CHART_COLORS[index % CHART_COLORS.length];
              const isVisible = visibleRestaurantIds[restaurant.restaurant_id];
              return (
                <label
                  key={restaurant.restaurant_id}
                  className={`z-report-chart-filter ${isVisible ? "is-active" : ""}`}
                  style={{ "--filter-color": color }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(isVisible)}
                    onChange={() => toggleRestaurant(restaurant.restaurant_id)}
                  />
                  <span className="z-report-chart-filter-dot" />
                  <span className="z-report-chart-filter-name">
                    {restaurant.restaurant_name}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="energy-chart-filter-block">
          <div className="energy-chart-filter-head">
            <span className="energy-chart-filter-label">Счётчики</span>
            <div className="z-report-chart-actions">
              <button
                type="button"
                className="z-report-ghost-btn"
                onClick={() => setAllDevicesVisible(true)}
              >
                Включить все
              </button>
              <button
                type="button"
                className="z-report-ghost-btn"
                onClick={() => setAllDevicesVisible(false)}
              >
                Выключить все
              </button>
            </div>
          </div>
          <div className="energy-chart-device-groups">
            {devicesByRestaurant.map((group) => (
              <div key={group.restaurant_id} className="energy-chart-device-group">
                <p className="energy-chart-device-group-title">
                  {group.restaurant_name}
                </p>
                <div className="z-report-chart-filters">
                  {group.devices.map((device) => {
                    const colorIndex =
                      deviceColorIndex[device.device_id] ?? 0;
                    const color = CHART_COLORS[colorIndex % CHART_COLORS.length];
                    const isVisible = visibleDeviceIds[device.device_id];
                    return (
                      <label
                        key={device.device_id}
                        className={`z-report-chart-filter ${
                          isVisible ? "is-active" : ""
                        }`}
                        style={{ "--filter-color": color }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(isVisible)}
                          onChange={() => toggleDevice(device.device_id)}
                        />
                        <span className="z-report-chart-filter-dot" />
                        <span className="z-report-chart-filter-name">
                          {device.device_name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {dailySeries.length === 0 || !dailyChartData.length ? (
          <p className="z-report-chart-empty">{dailyEmptyMessage}</p>
        ) : (
          <div className="z-report-chart-wrap">
            <ResponsiveContainer width="100%" height={360}>
              <LineChart
                data={dailyChartData}
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#dbe4f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatKwh}
                  width={72}
                />
                <Tooltip content={<ChartTooltip names={seriesNames} />} />
                {dailySeries.map((series) => (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={seriesNames[series.key]}
                    stroke={series.color}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="z-report-chart-section energy-chart-compare">
        <div className="z-report-chart-header">
          <h4 className="z-report-chart-title">
            Сравнение ресторанов за период
          </h4>
        </div>
        {comparisonData.length === 0 ? (
          <p className="z-report-chart-empty">
            Нет расхода по выбранным счётчикам
          </p>
        ) : (
          <div className="z-report-chart-wrap energy-chart-compare-wrap">
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={comparisonData}
                margin={{ top: 8, right: 16, left: 8, bottom: 64 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#dbe4f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-28}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatKwh}
                  width={72}
                />
                <Tooltip content={<CompareTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {comparisonData.map((item) => (
                    <Cell key={item.restaurant_id} fill={item.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </div>
  );
};

export default EnergyReportChart;
